import { Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Card, Col, Nav, Row, Table } from 'react-bootstrap'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import { ActiveInActive } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddExpedition from '../../../components/modal/expeditionrent/ModalAddExpedition'
import ModalAddRent from '../../../components/modal/expeditionrent/ModalAddRent'
import ModalUpdateExpedition from '../../../components/modal/expeditionrent/ModalUpdateExpedition'
import ModalUpdateRent from '../../../components/modal/expeditionrent/ModalUpdateRent'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'

const MasterExpedition = () => {
  const headerTitleStyle = {
    color: 'var(--primary-orange)',
    fontSize: '24px',
    fontWeight: '600'
  }

  const tableHeaderStyle = {
    backgroundColor: '#f8f9fa',
    color: '#333333',
    fontWeight: '700',
    borderBottom: '1px solid #e9ecef',
    textTransform: 'uppercase',
    fontSize: '0.85rem'
  }

  const [modalAddExpedition, setModalAddExpedition] = useState(false)
  const [modalAddRent, setModalAddRent] = useState(false)
  const [modalUpdateExpedition, setModalUpdateExpedition] = useState(false)
  const [modalUpdateRent, setModalUpdateRent] = useState(false)
  const [editId, setEditId] = useState(0)
  const [data, setData] = useState([])
  const [activeKey, setActiveKey] = useState('expedition')
  const [isLoading, setIsLoading] = useState(false)

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const { per_page, current_page } = meta
  const prevActiveKey = useRef(activeKey)
  const abortControllerRef = useRef(null)

  const loadData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setIsLoading(true)
    try {
      const endpoint =
        activeKey === 'expedition'
          ? 'master/expedition/list'
          : 'master/expedition/rents'
      const res = await api.get(endpoint, {
        signal,
        params: { per_page, page: current_page }
      })

      const { metadata, data: initialData } = res.data
      setData(initialData)
      setMeta(metadata)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (prevActiveKey.current !== activeKey) {
      prevActiveKey.current = activeKey
      if (current_page !== 1) {
        setMeta((prev) => ({ ...prev, current_page: 1 }))
        return
      }
    }
    loadData()

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, per_page, current_page])

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
    window.scrollTo(0, 0)
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(row) {
    setEditId(row.id)
    if (activeKey === 'expedition') {
      setModalUpdateExpedition(true)
    } else {
      setModalUpdateRent(true)
    }
  }

  const handleSelectActive = (key) => {
    setActiveKey(key)
    setData([])
  }

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Master Expedition & Rent</h3>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Nav
            variant="underline"
            onSelect={handleSelectActive}
            defaultActiveKey={activeKey}
            className="border-bottom"
          >
            <Nav.Item>
              <Nav.Link
                eventKey={'expedition'}
                className={`text-uppercase fw-bold ${
                  activeKey === 'expedition'
                    ? 'text-primary-orange'
                    : 'text-muted'
                }`}
              >
                Expedition
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey={'rent'}
                className={`text-uppercase fw-bold ${
                  activeKey === 'rent' ? 'text-primary-orange' : 'text-muted'
                }`}
              >
                Rent
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      <Row className="align-items-center mb-3">
        <Col>
          <h4
            className="text-muted text-uppercase mb-0"
            style={{ fontSize: '14px' }}
          >
            {activeKey === 'expedition' ? 'Expedition List' : 'Rent List'}
          </h4>
        </Col>
        <Col className="text-end">
          <button
            className="btn btn-primary-orange shadow-sm rounded px-4"
            onClick={() =>
              activeKey === 'expedition'
                ? setModalAddExpedition(true)
                : setModalAddRent(true)
            }
          >
            Add {activeKey === 'expedition' ? 'Expedition' : 'Rent'}
          </button>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table responsive hover className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle">Name</th>
                  <th className="p-2 align-middle">Address</th>
                  <th className="p-2 align-middle">Phone Number</th>
                  <th className="p-2 align-middle text-end">Status</th>
                  <th className="p-2 align-middle text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!isLoading && !data.length && (
                  <tr>
                    <td colSpan={5}>
                      <RowEmpty colSpan={5} />
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  data.map((item, i) => (
                    <tr key={item.id || i}>
                      <td className="p-2 align-middle fw-medium text-dark">
                        {item?.name}
                      </td>
                      <td className="p-2 align-middle text-muted">
                        {item?.address}
                      </td>
                      <td className="p-2 align-middle text-muted">
                        {item?.phone}
                      </td>
                      <td className="p-2 align-middle text-end">
                        <ActiveInActive
                          text={item.status ? 'active' : 'inactive'}
                        />
                      </td>
                      <td className="p-2 align-middle text-end">
                        <TableAction>
                          <button
                            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => handleEdit(item)}
                          >
                            <Pencil size={13} />
                          </button>
                        </TableAction>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            {data.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handleOnChangeMeta={handleOnChangeMeta}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <ModalAddExpedition
        show={modalAddExpedition}
        onHide={() => setModalAddExpedition(false)}
        refreshData={() => loadData()}
      />
      <ModalAddRent
        show={modalAddRent}
        onHide={() => setModalAddRent(false)}
        refreshData={() => loadData()}
      />

      <ModalUpdateExpedition
        show={modalUpdateExpedition}
        onHide={() => setModalUpdateExpedition(false)}
        id={editId}
        refreshData={() => loadData()}
      />
      <ModalUpdateRent
        show={modalUpdateRent}
        onHide={() => setModalUpdateRent(false)}
        id={editId}
        refreshData={() => loadData()}
      />
    </div>
  )
}

export default MasterExpedition
